import { useTranslation } from 'react-i18next'

import { Box, HStack, Text } from '@enechain/ecloud-designsystem'
import { FaClockRegular } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

type Props = {
  /** 残り時間。 `~/models/gcRemainingTime` の `formatGcRemainingTime` の結果 (`HH:MM:SS` / `N日 HH:MM:SS`)。 */
  remaining: string
  /**
   * 次の GC のコマ番号と時間帯 (例: `{ koma: 38, range: '18:30〜19:00' }`)。 表記
   * (「コマ 38 (18:30〜19:00)」) はこの部品が `common:gcCountdown.slot` で組む — 画面ごとに組むと
   * 文言や補間を直した時に一部だけ取り残され、 統合した形がまた割れるため。
   * 省略すると区切り・「次の GC」・コマを出さない (時間前入札の受付開始までのモード。 AC-BIN-001 /
   * AC-BIN-089c: 受付開始は受渡日全体に掛かる制約で、 特定コマの締切ではない)。
   */
  slot?: { koma: number; range: string }
  /**
   * 残り時間の直前のラベル。 既定は「GC まで」。 何を数えているかが変わる時
   * (時間前入札の受付開始まで) だけ差し替える。
   */
  remainingLabel?: string
  /** 残り 10 分以下。 残り時間だけを negative の文字色にする (design `.gc-remaining.gc-urgent`)。 */
  urgent?: boolean
  /**
   * 終了時の文言 (GC 済 / 受付開始済 など)。 指定すると警告色を外して中立色にし、
   * この文言だけを出す (design `.gc-countdown--passed`。 警告色のまま残すと「まだ操作できる」 と読まれる)。
   */
  passedText?: string
}

/**
 * GC カウントダウン (全画面で 1 種)。 design `components.css` の `.gc-countdown`、
 * 辞書 `design/ui-feasibility/components/countdown.md` (2026-10-05 統合)。
 *
 * **[時計]「GC まで」 残り時間 | 「次の GC」 コマ・時間帯** の順に並べる。
 * 残り時間が主 (md / 600)、 「次の GC」 とコマ・時間帯が補足 (sm)。 主と補足の間に warning-mid の
 * 縦区切りを置く。 画面ごとに語順・文言・強調を変えない (2026-10-05 デザイナー判断「UI の
 * パターンを減らしたい」)。 統合前は 時間前入札 / 需要想定 当日 が「次の GC」 → コマ →
 * 「あと HH:MM:SS」、 同時同量 需要詳細 / 発電詳細 が KPI カード (`MM分SS秒`) と 2 通りに割れていた。
 *
 * 表示だけを持つ。 何を数えるか (次の GC の選び方) と残り時間の計算は画面側に置く —
 * 画面ごとに業務上の選び方が違う (時間前入札は境界ごとに次の GC を取り直す / 需要想定は
 * 固定コマを狙う / 同時同量は GC 済み境界から数える)。
 */
export const GcCountdownPill = ({
  remaining,
  slot,
  remainingLabel,
  urgent = false,
  passedText,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const isPassed = passedText !== undefined

  return (
    <HStack
      flexShrink={0}
      gap="6px"
      color={isPassed ? 'Text.midEmphasis' : 'Text.warningHigh'}
      lineHeight="1.3"
      background={isPassed ? 'Surface.secondary' : 'Surface.warningLow'}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={isPassed ? 'Border.lowEmphasis' : 'Border.warningMid'}
      borderRadius="full"
      boxShadow="0 1px 3px rgba(0, 0, 0, 0.08)"
      // DS に対応する shadow token が無いため design `.gc-countdown` の box-shadow を実測値で写す。
      whiteSpace="nowrap"
      paddingX="12px"
      paddingY="2px"
    >
      <FaClockRegular boxSize={iconSize.gridInline} aria-hidden="true" />
      {isPassed ? (
        // 終了: 状態の文言だけ (design `.gc-countdown--passed .gc-remaining` = sm / 400)。
        <Text as="span" textStyle="captionNormal" color="inherit">
          {passedText}
        </Text>
      ) : (
        <>
          <Text as="span" textStyle="captionNormal" color="inherit">
            {remainingLabel ?? t('common:gcCountdown.remainingLabel')}
          </Text>
          <Text
            as="span"
            textStyle="label"
            color={urgent ? 'Text.negativeHigh' : 'inherit'}
            fontVariantNumeric="tabular-nums"
          >
            {remaining}
          </Text>
          {slot !== undefined && (
            <>
              <Box
                width="1px"
                height="16px"
                aria-hidden="true"
                backgroundColor="Border.warningMid"
                marginX="2px"
              />
              <Text as="span" textStyle="captionNormal" color="inherit">
                {t('common:gcCountdown.nextGc')}
              </Text>
              <Text
                as="span"
                textStyle="captionStrong"
                color="inherit"
                fontVariantNumeric="tabular-nums"
              >
                {t('common:gcCountdown.slot', {
                  koma: slot.koma,
                  range: slot.range,
                })}
              </Text>
            </>
          )}
        </>
      )}
    </HStack>
  )
}
